import { getDefaultAsyncWrapperObj, ApiErrorType, AsyncRequestStatus } from "@ane/core/lib/models";
import { loadResourcesUtils } from "@ane/core/lib/utils/LoadResourcesUtils";
import ArmResources from "@ane/exp-data/lib/resources/ArmResources";
import MdsDataplaneResource from "@ane/exp-data/lib/resources/MdsDataplaneResource";
import EnvironmentService from "@ane/core/lib/services/EnvironmentService";
import { getWorkspaceBaseUrl } from "@ane/exp-data/lib/utils/StudioRoute.utils";
import NavigationService from "@ane/core/lib/services/NavigationService";
import TenantActions from "../ducks/TenantActions.js";
import { loadRootWorkspaceEpicReducer } from "@ane/exp-ux/lib/components/GetWorkspace/GetWorkspace.epics";
import { RETURN_URL, GROUP_ID, WORKSPACE_ID } from "@ane/core/lib/constants/QueryString.constants";
import { getHostname } from "@ane/core/lib/utils/UrlUtils";
import { ALLOW_HOST } from "./utils/WorkspaceSelector.constants.js";
import WPSResource from "@ane/exp-data/lib/resources/WPSResource";
import { setUserAffinity } from "@ane/exp-data/lib/userDetails/GetUserAffinity.utils";
const { updateCurrentManagementGroup } = TenantActions;
const actionPrefix = "WorkspaceSelector";
const loadSubscriptionsActionName = `${actionPrefix}/loadSubscriptions`;
const loadWorkspacesActionName = `${actionPrefix}/loadWorkspaces`;
const loadTenantsActionName = `${actionPrefix}/LOAD_TENANTS`;
const loadCtGroupsActionName = `${actionPrefix}/loadCtGroups`;
const loadRootGroupActionName = `${actionPrefix}/loadRootGroup`;
const loadWPSWorkspacesActionName = `${actionPrefix}/loadWPSWorkspaces`;
const loadWPSGroupsActionName = `${actionPrefix}/loadWPSGroups`;
const defaultStudioWorkspaceSelectionReducerState = {
  subscriptionsAsync: getDefaultAsyncWrapperObj(),
  workspacesAsync: getDefaultAsyncWrapperObj(),
  tenantsAsync: getDefaultAsyncWrapperObj(),
  ctGroupsAsync: { "0": getDefaultAsyncWrapperObj() },
  rootWorkspaceGroupAsync: getDefaultAsyncWrapperObj(),
  wpsWorkspacesAsync: getDefaultAsyncWrapperObj(),
  wpsGroupsAsync: getDefaultAsyncWrapperObj()
};
const subscriptionsLoadEpicReducerArg = {
  actionPrefix: loadSubscriptionsActionName,
  reduxKeyNested: () => ["subscriptionsAsync"],
  loadResource: () => ArmResources.getSubscriptions(),
  onCompleteSuccess: (resourcesResult) => resourcesResult.value
};
const subscriptionsLoadEpicReducer = loadResourcesUtils(subscriptionsLoadEpicReducerArg);
const workspaceLoadEpicReducerArg = {
  actionPrefix: loadWorkspacesActionName,
  customError: (inputPayload, response) => {
    if (response.statusCode >= 400 && response.statusCode < 500) {
      return {
        correlationId: "https://expdocs.azurewebsites.net/",
        type: ApiErrorType.UserError,
        displayError: `This subscription does not contain any Experimentation workspaces. Ask your admin for help or follow the doc <a href="https://expdocs.azurewebsites.net/" target="_blank">https://expdocs.azurewebsites.net/</a> to create a workspace`
      };
    } else {
      return {
        correlationId: "",
        type: ApiErrorType.UnknownError,
        displayError: `Unknown error`
      };
    }
  },
  reduxKeyNested: () => ["workspacesAsync"],
  loadResource: (inputPayload) => ArmResources.getWorkspaces(inputPayload.subscriptionId),
  onCompleteSuccess: (resourcesResult) => resourcesResult.value
};
const workspaceLoadEpicReducer = loadResourcesUtils(workspaceLoadEpicReducerArg);
const ctGroupLoadEpicReducerArg = {
  actionPrefix: loadCtGroupsActionName,
  reduxKeyNested: (inputPayload) => ["ctGroupsAsync", inputPayload.workspaceId],
  loadResource: (inputPayload) => MdsDataplaneResource.getExperimentationGroups(inputPayload.workspaceId),
  onCompleteSuccess: (resourcesResult) => resourcesResult,
  dontLoad: (inputPayload, state) => {
    var _a, _b, _c;
    return ((_c = (_b = (_a = state.workspaceSelector.ctGroupsAsync[inputPayload.workspaceId]) == null ? void 0 : _a.data) == null ? void 0 : _b.length) != null ? _c : 0) > 0;
  }
};
const ctGroupLoadEpicReducer = loadResourcesUtils(ctGroupLoadEpicReducerArg);
const rootGroupLoadEpicReducerArg = {
  actionPrefix: loadRootGroupActionName,
  reduxKeyNested: () => ["rootWorkspaceGroupAsync"],
  loadResource: (inputPayload) => MdsDataplaneResource.getRootExperimentationGroup(inputPayload.newValues.workspaceId),
  onCompleteSuccess: (resourcesResult) => {
    return resourcesResult;
  },
  afterActionsOnSuccess: (inputPayload, output) => {
    if (output == null ? void 0 : output.IsVNextExpMan) {
      onCompleteGroupSelection(inputPayload.newValues, true);
      return [
        loadRootWorkspaceEpicReducer.actionCreators.loaded(
          { workspaceId: inputPayload.newValues.workspaceId },
          { data: output, status: AsyncRequestStatus.Completed }
        )
      ];
    } else {
      return [
        updateCurrentManagementGroup(inputPayload.newValues.workspaceId, inputPayload.newValues.groupId, () => {
          onCompleteGroupSelection(inputPayload.newValues, false);
        })
      ];
    }
  }
};
const rootGroupLoadEpicReducer = loadResourcesUtils(rootGroupLoadEpicReducerArg);
const onCompleteGroupSelection = (newValues, isStudio) => {
  const { workspaceId, groupId } = newValues;
  setUserAffinity(workspaceId, groupId);
  returnBack(newValues, isStudio);
};
const returnBack = (output, isStudio) => {
  var _a;
  let returnUrlQueryParam = EnvironmentService.getQueryParam(RETURN_URL);
  if (!!returnUrlQueryParam) {
    returnUrlQueryParam = decodeURI(returnUrlQueryParam);
    const returnUrlHostName = getHostname(returnUrlQueryParam).toLowerCase();
    if (!ALLOW_HOST.map((h) => h.toLowerCase()).includes(returnUrlHostName)) {
      returnUrlQueryParam = "";
    }
  }
  let returnUrl = !!returnUrlQueryParam ? `${returnUrlQueryParam}` : `${isStudio ? getWorkspaceBaseUrl((_a = output == null ? void 0 : output.workspaceId) != null ? _a : "") : "/"}`;
  returnUrl = NavigationService.getCustomQueryParamsOmitingSomeParams(returnUrl, [GROUP_ID, WORKSPACE_ID]);
  if (!!output) {
    if (!isStudio || !!returnUrlQueryParam) {
      returnUrl = NavigationService.addParamToHref({ href: returnUrl, param: WORKSPACE_ID, value: output.workspaceId });
      returnUrl = NavigationService.addParamToHref({ href: returnUrl, param: GROUP_ID, value: output.groupId });
    }
  }
  NavigationService.openExternalLinkInCurrentTab(returnUrl);
};
const loadTenantsEpicReducerArg = {
  actionPrefix: loadTenantsActionName,
  reduxKeyNested: () => ["tenantsAsync"],
  loadResource: () => ArmResources.getTenants(),
  onCompleteSuccess: (resourceResult) => resourceResult
};
const loadTenantsEpicReducer = loadResourcesUtils(loadTenantsEpicReducerArg);
const WPSWorkspacesLoadEpicReducerArg = {
  actionPrefix: loadWPSWorkspacesActionName,
  reduxKeyNested: () => ["wpsWorkspacesAsync"],
  loadResource: () => {
    return WPSResource.getWPSWorkspaces();
  },
  onCompleteSuccess: (resourcesResult) => resourcesResult.tenants
};
const WPSWorkspacesLoadEpicReducer = loadResourcesUtils(WPSWorkspacesLoadEpicReducerArg);
const WPSGroupsLoadEpicReducerArg = {
  actionPrefix: loadWPSGroupsActionName,
  reduxKeyNested: () => ["wpsGroupsAsync"],
  loadResource: (inputPayload) => WPSResource.getExperimentationGroups(inputPayload.workspaceId),
  onCompleteSuccess: (resourcesResult) => resourcesResult
};
const WPSGroupsLoadEpicReducer = loadResourcesUtils(WPSGroupsLoadEpicReducerArg);
const studioWorkspaceSelectionReducer = (state = defaultStudioWorkspaceSelectionReducerState, action) => {
  state = subscriptionsLoadEpicReducer.reduxChange(state, action);
  state = workspaceLoadEpicReducer.reduxChange(state, action);
  state = loadTenantsEpicReducer.reduxChange(state, action);
  state = ctGroupLoadEpicReducer.reduxChange(state, action);
  state = rootGroupLoadEpicReducer.reduxChange(state, action);
  state = WPSWorkspacesLoadEpicReducer.reduxChange(state, action);
  state = WPSGroupsLoadEpicReducer.reduxChange(state, action);
  switch (action.type) {
    default:
      return state;
  }
};
export {
  WPSGroupsLoadEpicReducer,
  WPSWorkspacesLoadEpicReducer,
  ctGroupLoadEpicReducer,
  studioWorkspaceSelectionReducer as default,
  defaultStudioWorkspaceSelectionReducerState,
  loadTenantsEpicReducer,
  onCompleteGroupSelection,
  returnBack,
  rootGroupLoadEpicReducer,
  studioWorkspaceSelectionReducer,
  subscriptionsLoadEpicReducer,
  workspaceLoadEpicReducer
};
//# sourceMappingURL=StudioWorkspaceSelector.ducks.js.map
